import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';

import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { TableHeader } from 'components/TableHeader';
import { REQUEST_LOADING, SUCCESS_DELETE } from 'constants/alerts';
import { useDrawers } from 'hooks/useDrawers';
import { usePagination } from 'hooks/usePagination';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { formatDate } from 'utils';
import { ServicesService } from 'services/ServicesService';
import { PromoTable } from './PromoTable';
import { PromoForm } from './PromoForm';

function Promo() {
  useDocumentTitle('Промо');
  const { setCount } = useHeaderChanger('Промо');
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const {
    state: { drawerVisible, activeRecord, formIsActive },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const { page, totalRows, perPage, setTotalRows, setPagination } =
    usePagination();

  useEffect(() => {
    fetchPromos();
  }, [page, perPage, searchQuery]);

  const fetchPromos = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await ServicesService.getPromos(
        page,
        perPage,
        searchQuery
      );

      setPromos(items);
      setCount(total);
      setTotalRows(totalRows);
    } catch (err) {
      message.error('Не удалось загрузить промо');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await ServicesService.deletePromo(activeRecord.group);

      if (isDeleted) {
        closeDrawer();
        fetchPromos();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error('Не удалось удалить промо');
    } finally {
      hide();
    }
  };

  const handleSearch = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  return (
    <>
      <TableHeader onCreate={openCreateForm} pageId="promo" />

      <PromoTable
        items={promos}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        onRowClick={openDrawer}
        onChange={setPagination}
        onSearch={handleSearch}
      />

      <PromoForm
        isActive={formIsActive}
        onClose={closeForm}
        onSuccess={fetchPromos}
        initialValues={activeRecord}
      />

      <RecordInfoDrawer
        record={activeRecord}
        pageId="promo"
        recordFields={[
          { label: 'Компания', key: 'company' },
          {
            label: 'Тип',
            key: 'type',
            computedValue:
              activeRecord?.type === 'unique' ? 'Уникальный' : 'Универсальный',
          },
          { label: 'Скидка (%)', key: 'percentage' },
          { label: 'Лимит', key: 'count' },
          {
            label: 'Дата начала',
            key: 'start_date',
            computedValue: formatDate(activeRecord?.start_date),
          },
          {
            label: 'Дата окончания',
            key: 'end_date',
            computedValue: formatDate(activeRecord?.end_date),
          },
          {
            label: 'Статус',
            key: 'isActive',
            computedValue: activeRecord?.isActive ? 'Активный' : 'Не активный',
          },
        ]}
        drawerTitle="Информация о промо"
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText="Вы точно хотите удалить это промо?"
      />
    </>
  );
}

export { Promo };
